import { camelCase, snakeCase } from 'lodash-es';
import { TRANSPORT } from '../../service/enums.js';
import { removeUndefined } from '../../utils/objects.js';
import { BaseMessage } from '../BaseMessage.js';
import MessageDescriptor from '../MessageDescriptor.js';
import BaseField from './BaseField.js';
import ConstField from './ConstField.js';
const mapMessagesWithDiscriminator = (messages, discriminator) => {
    if (!discriminator) {
        throw new Error('Invalid discriminator');
    }
    return Object.values(messages).reduce((mapping, message) => {
        const messageDescriptor = MessageDescriptor.load(message);
        const discriminatorField = messageDescriptor.fieldsMetadata[discriminator];
        if (!discriminatorField) {
            throw new Error(`"${message.name}" does not have "${discriminator}" field or it's not a const`);
        }
        if (!(discriminatorField instanceof ConstField)) {
            throw new TypeError(`"${message.name}" "${discriminator}" field must be a ConstField`);
        }
        const messageId = discriminatorField.const.toString();
        if (mapping.schema[messageId]) {
            throw new Error(`"${message.name}" "${discriminator}" field must be a unique value`);
        }
        mapping.schema[messageId] = `#/components/schemas/${message.name}`;
        mapping.proto[messageId] = message.name;
        mapping.messages[messageId] = message;
        mapping.protoMessages[camelCase(messageId)] = message;
        return mapping;
    }, { schema: {}, proto: {}, messages: {}, protoMessages: {} });
};
class OneOfField extends BaseField {
    oneOf;
    discriminator;
    discriminatorCamelCase;
    mapping;
    constructor(data) {
        super({
            ...data,
        });
        this.oneOf = data.oneOf;
        this.discriminator = data.discriminator;
        this.discriminatorCamelCase = camelCase(data.discriminator);
        this.references = data.oneOf.map((message) => MessageDescriptor.load(message));
        this.mapping = mapMessagesWithDiscriminator(this.oneOf, this.discriminator);
        this.proto.definition = [
            'message [[name.pc]]OneOfWrapper {',
            `  oneof ${snakeCase(this.discriminator)} {`,
            ...Object.entries(this.mapping.proto).map(([messageId, messageName], index) => `    ${messageName} ${snakeCase(messageId)} = ${index + 1};`),
            '  }',
            '}',
        ];
        this.proto.type = '[[name.pc]]OneOfWrapper';
    }
    getExample(transport) {
        const innerExample = this.references[0].getExample(transport);
        if (transport === TRANSPORT.GRPC) {
            const restExample = this.references[0].getExample(TRANSPORT.REST);
            const valueCamelCase = camelCase(restExample[this.discriminator]);
            return {
                [this.discriminatorCamelCase]: valueCamelCase,
                [valueCamelCase]: innerExample,
            };
        }
        return innerExample;
    }
    toSchema(transport, options) {
        if (transport === TRANSPORT.GRPC) {
            return removeUndefined({
                ...super.toSchema(transport, options),
                type: 'object',
                oneOf: Object.entries(this.mapping.schema).map(([discriminatorValue, reference]) => {
                    const valueCamelCase = camelCase(discriminatorValue);
                    return {
                        type: 'object',
                        properties: {
                            [this.discriminatorCamelCase]: {
                                type: 'string',
                                enum: [valueCamelCase],
                            },
                            [valueCamelCase]: {
                                $ref: reference,
                            },
                        },
                        required: [this.discriminatorCamelCase, valueCamelCase],
                    };
                }),
                discriminator: {
                    propertyName: this.discriminatorCamelCase,
                },
            });
        }
        else {
            return removeUndefined({
                ...super.toSchema(transport, options),
                type: 'object',
                oneOf: this.oneOf.map((message) => ({
                    $ref: `#/components/schemas/${message.name}`,
                })),
                discriminator: {
                    propertyName: this.discriminator,
                    mapping: this.mapping.schema,
                },
            });
        }
    }
    toJSON(input, target) {
        const valueJson = input.toJSON(target);
        if (target === TRANSPORT.GRPC) {
            const discriminatorValue = camelCase(valueJson[this.discriminator]);
            return {
                [this.discriminatorCamelCase]: discriminatorValue,
                [discriminatorValue]: valueJson,
            };
        }
        return valueJson;
    }
    fromJSON(input) {
        if (typeof input === 'undefined' || input instanceof BaseMessage) {
            return input;
        }
        const restDiscriminatorValue = input[this.discriminator];
        const message = this.mapping.messages[restDiscriminatorValue];
        const data = input;
        if (!message) {
            throw new Error('Invalid JSON data message');
        }
        return message.create(data);
    }
    fromProto(input) {
        if (typeof input === 'undefined' || input instanceof BaseMessage) {
            return input;
        }
        const grpcDiscriminatorValue = input[this.discriminatorCamelCase];
        const message = this.mapping.protoMessages[grpcDiscriminatorValue];
        const data = input[grpcDiscriminatorValue];
        if (!message) {
            throw new Error('Invalid PROTO data message');
        }
        return message.fromProto(data);
    }
}
export default OneOfField;
//# sourceMappingURL=OneOfField.js.map